import { Button, Input, Dropdown, Option, makeStyles, Card, MessageBar, MessageBarBody, Combobox, Spinner, Label, tokens } from '@fluentui/react-components';
import { Dismiss24Regular, Search24Regular, Tag24Regular, DocumentLink24Regular, Class24Regular, ArrowDownload24Regular } from '@fluentui/react-icons';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import { useState, useEffect } from 'react';
import { educationService } from '../../services/educationService';
import type { EducationUser, EducationClass } from '../../types';

export interface TrackingSearchParams {
  // Campos comuns
  correlationId?: string;
  operation?: string;
  userType?: string;  // Tipo de Usuário (Estudante, Professor, etc.) - só modo 'user'
  status?: string;    // Status final da operação (Sucesso, Erro, Retry, Ignorado)
  dateFrom?: Date;
  dateTo?: Date;
  // Campos modo 'user'
  searchTerm?: string;
  externalId?: string;
  // Campos modo 'class'
  sectionId?: string;
  className?: string;
  classCode?: string;
}

interface TrackingFiltersProps {
  mode?: 'user' | 'class';  // default: 'user'
  onSearch: (params: TrackingSearchParams) => void;
  onClear?: () => void;
  onExport?: () => void;
  loading?: boolean;
  exporting?: boolean;
}

// Função para formatar data para datetime-local input (reservada para uso futuro)
/* istanbul ignore next */
export const formatDateTimeLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

// Datas padrão: VAZIO (para TOP 50 na carga inicial)
// Se o usuário quiser filtrar por data, deve preencher manualmente
const getDefaultDates = () => {
  return {
    startDate: null as Date | null,
    endDate: null as Date | null,
    startTime: '',
    endTime: ''
  };
};

const useStyles = makeStyles({
  container: {
    marginBottom: '24px',
  },
  card: {
    padding: '20px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  searchForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  searchRow: {
    display: 'flex',
    gap: '16px',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
  },
  inputContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: '200px',
  },
  inputContainerWide: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: '250px',
    flex: 1,
  },
  dateInputContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: '160px',
  },
  timeInputContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    width: '90px',
  },
  dateTimeGroup: {
    display: 'flex',
    gap: '8px',
    alignItems: 'flex-end',
  },
  dropdownContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: '180px',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-end',
  },
  errorMessage: {
    marginTop: '12px',
  },
});

export const TrackingFilters: React.FC<TrackingFiltersProps> = ({ mode = 'user', onSearch, onClear, onExport, loading, exporting }) => {
  const styles = useStyles();

  const defaultDates = getDefaultDates();

  // Estados comuns
  const [correlationId, setCorrelationId] = useState('');
  const [operation, setOperation] = useState('');
  const [userType, setUserType] = useState('');  // Tipo de Usuário
  const [status, setStatus] = useState('');      // Status da operação

  // Estados modo 'user'
  const [searchTerm, setSearchTerm] = useState('');
  const [externalId, setExternalId] = useState('');
  const [nameSuggestions, setNameSuggestions] = useState<EducationUser[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  // Estados modo 'class'
  const [sectionId, setSectionId] = useState('');
  const [className, setClassName] = useState('');
  const [classCode, setClassCode] = useState('');
  const [classSuggestions, setClassSuggestions] = useState<EducationClass[]>([]);
  const [loadingClassSuggestions, setLoadingClassSuggestions] = useState(false);

  // Datas e horários (separados para usar DatePicker do Fluent UI)
  const [startDate, setStartDate] = useState<Date | null>(defaultDates.startDate);
  const [endDate, setEndDate] = useState<Date | null>(defaultDates.endDate);
  const [startTime, setStartTime] = useState(defaultDates.startTime);
  const [endTime, setEndTime] = useState(defaultDates.endTime);
  const [dateError, setDateError] = useState<string>('');

  // Função helper para combinar data e hora
  const combineDateAndTime = (date: Date | null, time: string): Date | undefined => {
    if (!date) return undefined;
    const result = new Date(date);
    if (time) {
      const [hours, minutes] = time.split(':').map(Number);
      result.setHours(hours || 0, minutes || 0, 0, 0);
    } else {
      result.setHours(0, 0, 0, 0);
    }
    return result;
  };

  // Validação de intervalo de datas (máximo 7 dias)
  useEffect(() => {
    if (startDate && endDate) {
      const dateFrom = combineDateAndTime(startDate, startTime);
      const dateTo = combineDateAndTime(endDate, endTime);

      if (dateFrom && dateTo) {
        const diffTime = Math.abs(dateTo.getTime() - dateFrom.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays > 7) {
          setDateError('O intervalo maximo entre as datas e de 7 dias.');
        } else if (dateFrom > dateTo) {
          setDateError('A data inicial nao pode ser maior que a data final.');
        } else {
          setDateError('');
        }
      }
    } else {
      setDateError('');
    }
  }, [startDate, endDate, startTime, endTime]);

  // Auto-busca de sugestões de nome - modo 'user' (debounce 300ms)
  useEffect(() => {
    if (mode !== 'user') return;

    const searchUsers = async () => {
      if (searchTerm.length < 2) {
        setNameSuggestions([]);
        return;
      }

      setLoadingSuggestions(true);
      try {
        const result = await educationService.getUsers(1, 10, searchTerm);
        setNameSuggestions(result.data);
      } catch (err) {
        console.error('Erro ao buscar usuarios:', err);
        setNameSuggestions([]);
      } finally {
        setLoadingSuggestions(false);
      }
    };

    const debounce = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounce);
  }, [searchTerm, mode]);

  // Auto-busca de sugestões de classes - modo 'class' (debounce 300ms)
  useEffect(() => {
    if (mode !== 'class') return;

    const searchClasses = async () => {
      if (className.length < 2) {
        setClassSuggestions([]);
        return;
      }

      setLoadingClassSuggestions(true);
      try {
        const result = await educationService.getClasses(1, 10, className);
        setClassSuggestions(result.data);
      } catch (err) {
        console.error('Erro ao buscar classes:', err);
        setClassSuggestions([]);
      } finally {
        setLoadingClassSuggestions(false);
      }
    };

    const debounce = setTimeout(searchClasses, 300);
    return () => clearTimeout(debounce);
  }, [className, mode]);

  // Verifica se algum filtro de identificação está preenchido (para busca ampla)
  const hasIdentificationFilter = () => {
    if (mode === 'class') {
      return !!(sectionId.trim() || className.trim() || classCode.trim() || correlationId.trim());
    }
    return !!(searchTerm.trim() || externalId.trim() || correlationId.trim());
  };

  // Handler para seleção de classe no autocomplete
  const handleClassSelect = (cls: EducationClass) => {
    setClassName(cls.displayName);
    // Remove o prefixo "sec_" do externalId para facilitar a busca
    const cleanId = cls.externalId.replace(/^sec_/i, '');
    setSectionId(cleanId);
    if (cls.classCode) {
      setClassCode(cls.classCode);
    }
    setClassSuggestions([]);
  };

  const handleSearch = () => {
    // Bloqueia busca se houver erro de data
    if (dateError) {
      return;
    }

    const dateFrom = combineDateAndTime(startDate, startTime);
    const dateTo = combineDateAndTime(endDate, endTime);

    // Monta parâmetros de busca com campos comuns
    const params: TrackingSearchParams = {
      correlationId: correlationId.trim() || undefined,
      operation: operation || undefined,
      userType: mode === 'user' ? (userType || undefined) : undefined,
      status: status || undefined,
      dateFrom,
      dateTo,
    };

    // Adiciona campos específicos do modo
    if (mode === 'user') {
      params.searchTerm = searchTerm.trim() || undefined;
      params.externalId = externalId.trim() || undefined;
    } else {
      params.sectionId = sectionId.trim() || undefined;
      params.className = className.trim() || undefined;
      params.classCode = classCode.trim() || undefined;
    }

    onSearch(params);
  };

  const handleClear = () => {
    const newDefaults = getDefaultDates();
    // Campos comuns
    setCorrelationId('');
    setOperation('');
    setUserType('');
    setStatus('');
    setStartDate(newDefaults.startDate);
    setEndDate(newDefaults.endDate);
    setStartTime(newDefaults.startTime);
    setEndTime(newDefaults.endTime);
    setDateError('');
    // Campos modo 'user'
    setSearchTerm('');
    setExternalId('');
    setNameSuggestions([]);
    // Campos modo 'class'
    setSectionId('');
    setClassName('');
    setClassCode('');
    setClassSuggestions([]);
    // Notifica a página para voltar ao estado inicial
    if (onClear) {
      onClear();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Helper para obter o externalId do usuário
  const getUserExternalId = (user: EducationUser): string | undefined => {
    return user.student?.externalId || user.teacher?.externalId;
  };

  // Handler para seleção de usuário no autocomplete
  const handleUserSelect = (user: EducationUser) => {
    setSearchTerm(user.displayName);
    // Se o usuário tem externalId, preenche automaticamente
    const extId = getUserExternalId(user);
    if (extId) {
      setExternalId(extId);
    }
    setNameSuggestions([]);
  };

  return (
    <div className={styles.container}>
      <Card className={styles.card}>
        <div className={styles.searchForm}>
          {/* Linha 1: Campos de identificação (condicionais por modo) */}
          <div className={styles.searchRow}>
            {mode === 'user' ? (
              <>
                {/* MODO USER: Nome/Email com autocomplete */}
                <div className={styles.inputContainerWide}>
                  <Label htmlFor="searchTerm">Nome / Email</Label>
                  <Combobox
                    id="searchTerm"
                    placeholder="Nome ou email..."
                    value={searchTerm}
                    onInput={(e) => setSearchTerm((e.target as HTMLInputElement).value)}
                    onOptionSelect={(_, data) => {
                      const selected = nameSuggestions.find(u => u.id === data.optionValue);
                      if (selected) {
                        handleUserSelect(selected);
                      }
                    }}
                    freeform
                  >
                    {loadingSuggestions && (
                      <Option key="loading" value="" text="Buscando..." disabled>
                        <Spinner size="tiny" style={{ marginRight: '8px' }} />
                        Buscando...
                      </Option>
                    )}
                    {!loadingSuggestions && nameSuggestions.length === 0 && searchTerm.length >= 2 && (
                      <Option key="empty" value="" text="Nenhum usuario encontrado" disabled>
                        Nenhum usuario encontrado
                      </Option>
                    )}
                    {nameSuggestions.map((user) => {
                      const extId = getUserExternalId(user);
                      return (
                        <Option key={user.id} value={user.id} text={user.displayName}>
                          {user.displayName} ({extId || 'sem ID'})
                        </Option>
                      );
                    })}
                  </Combobox>
                </div>

                {/* MODO USER: External ID */}
                <div className={styles.inputContainer}>
                  <Label htmlFor="externalId">External ID</Label>
                  <Input
                    id="externalId"
                    placeholder="1234567890..."
                    value={externalId}
                    onChange={(_, data) => setExternalId(data.value)}
                    onKeyPress={handleKeyPress}
                    contentBefore={<Tag24Regular />}
                  />
                </div>
              </>
            ) : (
              <>
                {/* MODO CLASS: Nome da Classe com autocomplete */}
                <div className={styles.inputContainerWide}>
                  <Label htmlFor="className">Nome da Classe</Label>
                  <Combobox
                    id="className"
                    placeholder="Digite para buscar..."
                    value={className}
                    onInput={(e) => setClassName((e.target as HTMLInputElement).value)}
                    onOptionSelect={(_, data) => {
                      const selected = classSuggestions.find(c => c.externalId === data.optionValue);
                      if (selected) {
                        handleClassSelect(selected);
                      }
                    }}
                    freeform
                  >
                    {loadingClassSuggestions && (
                      <Option key="loading" value="" text="Buscando..." disabled>
                        <Spinner size="tiny" style={{ marginRight: '8px' }} />
                        Buscando...
                      </Option>
                    )}
                    {!loadingClassSuggestions && classSuggestions.length === 0 && className.length >= 2 && (
                      <Option key="empty" value="" text="Nenhuma classe encontrada" disabled>
                        Nenhuma classe encontrada
                      </Option>
                    )}
                    {classSuggestions.map((cls) => (
                      <Option key={cls.externalId} value={cls.externalId} text={cls.displayName}>
                        {cls.displayName} ({cls.externalId})
                      </Option>
                    ))}
                  </Combobox>
                </div>

                {/* MODO CLASS: ID da Classe */}
                <div className={styles.inputContainer}>
                  <Label htmlFor="sectionId">ID da Classe</Label>
                  <Input
                    id="sectionId"
                    placeholder="1234567890..."
                    value={sectionId}
                    onChange={(_, data) => setSectionId(data.value)}
                    onKeyPress={handleKeyPress}
                    contentBefore={<Class24Regular />}
                  />
                </div>

                {/* MODO CLASS: Código da Turma */}
                <div className={styles.inputContainer}>
                  <Label htmlFor="classCode">Código da Turma</Label>
                  <Input
                    id="classCode"
                    placeholder="1234567890..."
                    value={classCode}
                    onChange={(_, data) => setClassCode(data.value)}
                    onKeyPress={handleKeyPress}
                    contentBefore={<Tag24Regular />}
                  />
                </div>
              </>
            )}

            {/* Campo CorrelationId (comum aos dois modos) */}
            <div className={styles.inputContainer}>
              <Label htmlFor="correlationId">CorrelationId</Label>
              <Input
                id="correlationId"
                placeholder="Busca tecnica avancada"
                value={correlationId}
                onChange={(_, data) => setCorrelationId(data.value)}
                onKeyPress={handleKeyPress}
                contentBefore={<DocumentLink24Regular />}
              />
            </div>
          </div>

          {/* Linha 2: Operacao, Tipo Mensagem (só user), Nivel Log */}
          <div className={styles.searchRow}>
            {/* Operation Dropdown - diferente por modo */}
            <div className={styles.dropdownContainer}>
              <Label>Operacao</Label>
              <Dropdown
                placeholder="Operacao"
                value={operation ? getOperationLabel(operation) : ''}
                onOptionSelect={(_, data) => setOperation(data.optionValue || '')}
              >
                <Option value="">Todas</Option>
                {mode === 'user' ? (
                  <>
                    {/* Operações de usuário */}
                    <Option value="NewStudent">Novo Estudante</Option>
                    <Option value="UpdateStudent">Atualizar Estudante</Option>
                    <Option value="DeleteStudent">Excluir Estudante</Option>
                    <Option value="NewTeacher">Novo Professor</Option>
                    <Option value="UpdateTeacher">Atualizar Professor</Option>
                    <Option value="DeleteTeacher">Excluir Professor</Option>
                    <Option value="NewSchool">Nova Escola</Option>
                    <Option value="UpdateSchool">Atualizar Escola</Option>
                    <Option value="DeleteSchool">Excluir Escola</Option>
                    <Option value="NewClass">Nova Turma</Option>
                    <Option value="UpdateClass">Atualizar Turma</Option>
                    <Option value="DeleteClass">Excluir Turma</Option>
                    <Option value="NewStaff">Novo Funcionario</Option>
                    <Option value="UpdateStaff">Atualizar Funcionario</Option>
                    <Option value="DeleteStaff">Excluir Funcionario</Option>
                    <Option value="NewStudentEnrollment">Nova Matricula</Option>
                    <Option value="DeleteStudentEnrollment">Excluir Matricula</Option>
                    <Option value="NewTeacherRoster">Nova Atribuicao Professor</Option>
                    <Option value="DeleteTeacherRoster">Excluir Atribuicao Professor</Option>
                    <Option value="NewStaffRoster">Nova Atribuicao Staff</Option>
                    <Option value="DeleteStaffRoster">Excluir Atribuicao Staff</Option>
                  </>
                ) : (
                  <>
                    {/* Operações específicas de classe */}
                    <Option value="NewClass">Nova Turma</Option>
                    <Option value="UpdateClass">Atualizar Turma</Option>
                    <Option value="DeleteClass">Excluir Turma</Option>
                    <Option value="NewStudentEnrollment">Matricula de Aluno</Option>
                    <Option value="DeleteStudentEnrollment">Remocao de Aluno</Option>
                    <Option value="NewTeacherRoster">Atribuicao de Professor</Option>
                    <Option value="DeleteTeacherRoster">Remocao de Professor</Option>
                    <Option value="NewStaffRoster">Atribuicao de Staff</Option>
                    <Option value="DeleteStaffRoster">Remocao de Staff</Option>
                  </>
                )}
              </Dropdown>
            </div>

            {/* Tipo de Usuário Dropdown - só aparece no modo user */}
            {mode === 'user' && (
              <div className={styles.dropdownContainer}>
                <Label>Tipo de Usuario</Label>
                <Dropdown
                  placeholder="Tipo de Usuario"
                  value={userType ? getUserTypeLabel(userType) : ''}
                  onOptionSelect={(_, data) => setUserType(data.optionValue || '')}
                >
                  <Option value="">Todos</Option>
                  <Option value="Student">Estudante</Option>
                  <Option value="Teacher">Professor</Option>
                  <Option value="Staff">Funcionario</Option>
                  <Option value="Class">Turma</Option>
                  <Option value="School">Escola</Option>
                  <Option value="StudentEnrollment">Matricula</Option>
                  <Option value="TeacherRoster">Atribuicao Professor</Option>
                  <Option value="StaffRoster">Atribuicao Staff</Option>
                </Dropdown>
              </div>
            )}

            {/* Status Dropdown */}
            <div className={styles.dropdownContainer}>
              <Label>Status</Label>
              <Dropdown
                placeholder="Status"
                value={status ? getStatusLabel(status) : ''}
                onOptionSelect={(_, data) => setStatus(data.optionValue || '')}
              >
                <Option value="">Todos</Option>
                <Option value="processing">Processando</Option>
                <Option value="success">Sucesso</Option>
                <Option value="error">Erro</Option>
                <Option value="retry">Reagendado</Option>
                <Option value="dlq">DLQ</Option>
              </Dropdown>
            </div>
          </div>

          {/* Linha 3: Data/Hora Inicial, Data/Hora Final, Botoes */}
          <div className={styles.searchRow}>
            {/* Data/Hora Inicial */}
            <div className={styles.dateTimeGroup}>
              <div className={styles.dateInputContainer}>
                <Label htmlFor="startDate">Data Inicial</Label>
                <DatePicker
                  id="startDate"
                  placeholder="Selecionar..."
                  value={startDate}
                  onSelectDate={(date) => setStartDate(date || null)}
                  formatDate={(date) => date ? date.toLocaleDateString('pt-BR') : ''}
                  parseDateFromString={(str) => {
                    const parts = str.split('/');
                    if (parts.length === 3) {
                      return new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
                    }
                    return null;
                  }}
                />
              </div>
              <div className={styles.timeInputContainer}>
                <Label htmlFor="startTime">Hora</Label>
                <Input
                  id="startTime"
                  type="time"
                  value={startTime}
                  onChange={(_, data) => setStartTime(data.value)}
                />
              </div>
            </div>

            {/* Data/Hora Final */}
            <div className={styles.dateTimeGroup}>
              <div className={styles.dateInputContainer}>
                <Label htmlFor="endDate">Data Final</Label>
                <DatePicker
                  id="endDate"
                  placeholder="Selecionar..."
                  value={endDate}
                  onSelectDate={(date) => setEndDate(date || null)}
                  formatDate={(date) => date ? date.toLocaleDateString('pt-BR') : ''}
                  parseDateFromString={(str) => {
                    const parts = str.split('/');
                    if (parts.length === 3) {
                      return new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
                    }
                    return null;
                  }}
                />
              </div>
              <div className={styles.timeInputContainer}>
                <Label htmlFor="endTime">Hora</Label>
                <Input
                  id="endTime"
                  type="time"
                  value={endTime}
                  onChange={(_, data) => setEndTime(data.value)}
                />
              </div>
            </div>

            {/* Botoes de acao */}
            <div className={styles.actions}>
              <Button
                appearance="primary"
                icon={<Search24Regular />}
                onClick={handleSearch}
                disabled={loading || !!dateError}
                title={hasIdentificationFilter() ? 'Buscar em todo historico' : 'Buscar no intervalo de datas'}
              >
                {hasIdentificationFilter() ? 'Buscar em Todo Historico' : 'Buscar'}
              </Button>
              <Button
                icon={<Dismiss24Regular />}
                onClick={handleClear}
                disabled={loading}
              >
                Limpar Filtros
              </Button>
              {onExport && (
                <Button
                  icon={<ArrowDownload24Regular />}
                  onClick={onExport}
                  disabled={loading || exporting}
                >
                  {exporting ? 'Exportando...' : 'Exportar CSV'}
                </Button>
              )}
            </div>
          </div>

          {/* Mensagem de erro de validacao de data */}
          {dateError && (
            <MessageBar intent="error" className={styles.errorMessage}>
              <MessageBarBody>{dateError}</MessageBarBody>
            </MessageBar>
          )}
        </div>
      </Card>
    </div>
  );
};

// Helper functions para labels
function getOperationLabel(operation: string): string {
  const labels: Record<string, string> = {
    // Student
    'NewStudent': 'NewStudent - Novo Estudante',
    'UpdateStudent': 'UpdateStudent - Atualizar Estudante',
    'DeleteStudent': 'DeleteStudent - Excluir Estudante',
    // Teacher
    'NewTeacher': 'NewTeacher - Novo Professor',
    'UpdateTeacher': 'UpdateTeacher - Atualizar Professor',
    'DeleteTeacher': 'DeleteTeacher - Excluir Professor',
    // School
    'NewSchool': 'NewSchool - Nova Escola',
    'UpdateSchool': 'UpdateSchool - Atualizar Escola',
    'DeleteSchool': 'DeleteSchool - Excluir Escola',
    // Class
    'NewClass': 'NewClass - Nova Turma',
    'UpdateClass': 'UpdateClass - Atualizar Turma',
    'DeleteClass': 'DeleteClass - Excluir Turma',
    // Staff
    'NewStaff': 'NewStaff - Novo Funcionário',
    'UpdateStaff': 'UpdateStaff - Atualizar Funcionário',
    'DeleteStaff': 'DeleteStaff - Excluir Funcionário',
    // Student Enrollment
    'NewStudentEnrollment': 'NewStudentEnrollment - Nova Matrícula',
    'DeleteStudentEnrollment': 'DeleteStudentEnrollment - Excluir Matrícula',
    // Teacher Roster
    'NewTeacherRoster': 'NewTeacherRoster - Nova Atribuição Professor',
    'DeleteTeacherRoster': 'DeleteTeacherRoster - Excluir Atribuição Professor',
    // Staff Roster
    'NewStaffRoster': 'NewStaffRoster - Nova Atribuição Staff',
    'DeleteStaffRoster': 'DeleteStaffRoster - Excluir Atribuição Staff',
  };
  return labels[operation] || operation;
}

function getUserTypeLabel(userType: string): string {
  const labels: Record<string, string> = {
    'Student': 'Estudante',
    'Teacher': 'Professor',
    'Staff': 'Funcionario',
    'Class': 'Turma',
    'School': 'Escola',
    'StudentEnrollment': 'Matricula',
    'TeacherRoster': 'Atribuicao Professor',
    'StaffRoster': 'Atribuicao Staff',
  };
  return labels[userType] || userType;
}

function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    'processing': 'Processando',
    'success': 'Sucesso',
    'error': 'Erro',
    'retry': 'Reagendado',
    'dlq': 'DLQ',
  };
  return labels[status] || status;
}
